import { ICalloutProps, useTheme } from '@fluentui/react';
import { IPIcon, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface IAutoSaveStatusLabelProps {
  status: AutoSaveStatus;
  savingText: string;
  savedText: string;
  errorText: string;
  // Why the save failed, shown through the info tooltip. Without it the icon is dropped, because an
  // info affordance with nothing behind it is worse than none.
  errorDetail?: string;
  // Set when the write itself landed but something around it stayed degraded. This is deliberately not an
  // error state: the content is stored, so the label keeps saying "saved" and only shifts colour and adds
  // an explanatory tooltip. A degraded save must never read as a failed one.
  warning?: string;
  // For hosts that render above a fixed overlay of their own (the docx editor), so the tooltip's
  // portaled Callout is not stacked behind that overlay.
  tooltipCalloutProps?: ICalloutProps;
}

// The inline "saving / saved / failed" marker next to a surface that persists on its own. Every
// autosaving surface in the app shows the same thing, so the shape lives here once: a spinner while
// writing, a green check when the write landed, and red text with an explanation when it did not.
// Callers pass their own strings - each screen owns its translation keys.
const AutoSaveStatusLabel = (props: IAutoSaveStatusLabelProps) => {
  const theme = useTheme();

  if (props.status === 'saving') {
    return (
      <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
        <IPSpinner size={IPSpinnerSize.small} />
        <IPText styles={{ root: { color: theme.semanticColors.bodySubtext } }}>{props.savingText}</IPText>
      </IPStack>
    );
  }

  if (props.status === 'saved') {
    const savedColor = props.warning ? theme.semanticColors.severeWarningIcon : theme.semanticColors.successIcon;

    return (
      <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
        <IPIcon
          iconName={props.warning ? 'Warning' : 'CheckMark'}
          styles={{ root: { color: savedColor, fontSize: 14 } }}
        />
        <IPText styles={{ root: { color: savedColor } }}>{props.savedText}</IPText>
        {props.warning && (
          <IPTooltipHost content={props.warning} calloutProps={props.tooltipCalloutProps}>
            <IPIcon iconName="Info" styles={{ root: { color: savedColor, fontSize: 14, cursor: 'help' } }} />
          </IPTooltipHost>
        )}
      </IPStack>
    );
  }

  if (props.status === 'error') {
    return (
      <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
        <IPText styles={{ root: { color: theme.semanticColors.errorText, fontWeight: 600 } }}>{props.errorText}</IPText>
        {props.errorDetail && (
          <IPTooltipHost content={props.errorDetail} calloutProps={props.tooltipCalloutProps}>
            <IPIcon
              iconName="Info"
              styles={{ root: { color: theme.semanticColors.errorText, fontSize: 14, cursor: 'help' } }}
            />
          </IPTooltipHost>
        )}
      </IPStack>
    );
  }

  return null;
};

export default AutoSaveStatusLabel;
